import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Button, Chip, CircularProgress, Stack, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { Refresh, FileDownload, Assessment } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

const today = () => new Date().toISOString().slice(0, 10);

// Disposition -> colour
const dispColor = (s: string) => /normal/i.test(s) ? 'success' : /cancel/i.test(s) ? 'default' : 'error';

export const CallReportsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [timeUnit, setTimeUnit] = useState<'sec' | 'min'>('sec');
  const [f, setF] = useState({ tenant_id: '', from: today(), to: today(), direction: 'outbound', search: '' });

  useEffect(() => {
    api.get('/tenants?limit=500').then(r => setCustomers(r.data.data || r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: '2000' };
      if (f.tenant_id) params.tenant_id = f.tenant_id;
      if (f.from) params.from = f.from + ' 00:00:00';
      if (f.to) params.to = f.to + ' 23:59:59';
      if (f.direction) params.direction = f.direction;
      if (f.search) params.search = f.search;
      const { data } = await api.get('/cdr/report/customer-calls?' + new URLSearchParams(params));
      setRows(data.data || []);
      setTotals(data.totals || {});
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Failed to load report', { variant: 'error' });
    } finally { setLoading(false); }
  }, [f, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const fmtDur = (s: number) => {
    s = Number(s) || 0;
    if (timeUnit === 'min') return (s / 60).toFixed(2);
    const m = Math.floor(s / 60), ss = s % 60;
    return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };
  const money = (n: any) => (parseFloat(n) || 0).toFixed(4);
  const code = (c: string) => (c || '').replace(/^\+/, '');

  const exportCsv = () => {
    const head = ['Date', 'Customer', 'Caller ID', 'Called Number', 'Code', 'Destination', 'Carrier', 'Billed Duration', 'Debit ($)', 'Cost ($)', 'Disposition'];
    const lines = rows.map(r => [
      new Date(r.start_time).toLocaleString(), r.customer_name || '', r.src_number || '', r.dst_number || '',
      code(r.code), r.destination || '', r.carrier || '', fmtDur(r.billable_duration),
      money(r.sell_cost), money(r.buy_cost), r.disposition || '',
    ].map(x => `"${String(x).replace(/"/g, '""')}"`).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `customer-calls-${f.from}_${f.to}.csv`; a.click();
  };

  const grandDur = useMemo(() => fmtDur(totals.billed_seconds), [totals, timeUnit]);

  return (
    <Box>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
        <Assessment color='primary' />
        <Typography variant='h5' fontWeight={700} sx={{ flexGrow: 1 }}>Call Reports — Customer Calls</Typography>
        <ToggleButtonGroup size='small' exclusive value={timeUnit} onChange={(_, v) => v && setTimeUnit(v)}>
          <ToggleButton value='sec'>mm:ss</ToggleButton>
          <ToggleButton value='min'>Minutes</ToggleButton>
        </ToggleButtonGroup>
        <Button size='small' startIcon={<FileDownload />} onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>
        <Button size='small' variant='contained' startIcon={loading ? <CircularProgress size={14} /> : <Refresh />} onClick={load}>Run</Button>
      </Stack>

      {/* filter bar */}
      <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size='small' label='Customer' value={f.tenant_id} onChange={e => setF({ ...f, tenant_id: e.target.value })} sx={{ minWidth: 220 }}>
            <MenuItem value=''>All customers</MenuItem>
            {customers.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select size='small' label='Direction' value={f.direction} onChange={e => setF({ ...f, direction: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value=''>Both</MenuItem>
            <MenuItem value='outbound'>Outbound</MenuItem>
            <MenuItem value='inbound'>Inbound</MenuItem>
          </TextField>
          <TextField size='small' type='date' label='From' InputLabelProps={{ shrink: true }} value={f.from} onChange={e => setF({ ...f, from: e.target.value })} />
          <TextField size='small' type='date' label='To' InputLabelProps={{ shrink: true }} value={f.to} onChange={e => setF({ ...f, to: e.target.value })} />
          <TextField size='small' label='Search number' placeholder='caller or called' value={f.search} onChange={e => setF({ ...f, search: e.target.value })} sx={{ minWidth: 200 }} />
        </Box>
      </Paper>

      {/* totals cards */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        {[
          { l: 'Calls', v: totals.calls ?? 0 },
          { l: 'Answered', v: totals.answered ?? 0 },
          { l: 'Billed Duration', v: grandDur },
          { l: 'Debit ($)', v: money(totals.debit) },
          { l: 'Cost ($)', v: money(totals.cost) },
          { l: 'Margin ($)', v: money(totals.margin) },
        ].map(c => (
          <Paper key={c.l} variant='outlined' sx={{ px: 2, py: 1, minWidth: 120 }}>
            <Typography variant='caption' color='text.secondary'>{c.l}</Typography>
            <Typography variant='h6' fontWeight={700}>{String(c.v)}</Typography>
          </Paper>
        ))}
      </Box>

      <TableContainer component={Paper} variant='outlined'>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {['Date', 'Customer', 'Caller ID', 'Called Number', 'Code', 'Destination', 'Carrier', 'Billed Duration', 'Debit ($)', 'Cost ($)', 'Disposition'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} align='center' sx={{ py: 4 }}><CircularProgress size={26} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={11} align='center' sx={{ py: 4, color: 'text.secondary' }}>No calls for the selected filters</TableCell></TableRow>
            ) : rows.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant='caption'>{new Date(r.start_time).toLocaleString()}</Typography></TableCell>
                <TableCell><Typography variant='body2' fontWeight={600}>{r.customer_name || '-'}</Typography></TableCell>
                <TableCell>{r.src_number || '-'}</TableCell>
                <TableCell>{r.dst_number || '-'}</TableCell>
                <TableCell>{code(r.code) || '-'}</TableCell>
                <TableCell>{r.destination || '-'}</TableCell>
                <TableCell>{r.carrier || '-'}</TableCell>
                <TableCell align='right'>{fmtDur(r.billable_duration)}</TableCell>
                <TableCell align='right'>{money(r.sell_cost)}</TableCell>
                <TableCell align='right'>{money(r.buy_cost)}</TableCell>
                <TableCell><Chip size='small' variant='outlined' color={dispColor(r.disposition) as any} label={r.disposition} /></TableCell>
              </TableRow>
            ))}
            {rows.length > 0 && (
              <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell colSpan={7} align='right'>Grand Total</TableCell>
                <TableCell align='right'>{grandDur}</TableCell>
                <TableCell align='right'>{money(totals.debit)}</TableCell>
                <TableCell align='right'>{money(totals.cost)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
